import {
  AppstoreOutlined,
  BellOutlined,
  CloseOutlined,
  ControlOutlined,
  DollarOutlined,
  HistoryOutlined,
  HomeOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { Menu } from 'antd';
import { useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import Flex from '~/common/components/display/Flex.tsx';
import Logo from '~/common/components/Logo.tsx';
import Heading from '~/common/components/typography/Heading.tsx';
import useLayout from '~/common/hooks/useLayout.ts';
import ProfileMenu from '~/common/layouts/ProfileMenu.tsx';
import { useAppSelector } from '~/common/stores';
import { ThemeMode } from '~/common/types/theme.type.ts';
import { breakpoint, breakpoints } from '~/common/utils/breakpoint.utils';
import { safeArea } from '~/common/utils/safe-area.utils';
import { COLORS, LAYOUT, SPACING } from '~/configs/theme';
import { STORE_CONSOLE_ROLES } from '~/features/admin/data/admin.data.ts';
import { AdminRole } from '~/features/admin/types/admin.type';

import type { MenuProps } from 'antd';

type MenuItem = Required<MenuProps>['items'][number];

const DEBUG_USER_ID = 'xgm_debug';

const getStoreConsoleMenuItems = (userId?: string): MenuItem[] => [
  {
    key: '/store',
    icon: <ShopOutlined />,
    label: '실시간 모니터링',
  },
  ...(userId === DEBUG_USER_ID
    ? [
        {
          key: '/store/analytics',
          icon: <DollarOutlined />,
          label: '매출 및 통계',
        },
      ]
    : []),
];

const getMenuItems = (role?: AdminRole, userId?: string): MenuItem[] => {
  if (role != null && STORE_CONSOLE_ROLES.includes(role)) {
    return getStoreConsoleMenuItems(userId);
  }

  const isSystemAdmin = role === AdminRole.MASTER || role === AdminRole.SUPER_ADMIN;

  return [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: '홈',
    },
    { type: 'divider' },
    {
      key: 'members',
      type: 'group',
      label: '회원',
      children: [
        { key: '/users', icon: <UserOutlined />, label: '회원' },
        { key: '/user-access-histories', icon: <HistoryOutlined />, label: '접속 이력' },
      ],
    },
    { type: 'divider' },
    {
      key: 'service',
      type: 'group',
      label: '서비스 운영',
      children: [
        { key: '/store-managers', icon: <TeamOutlined />, label: '점주 / 매니저' },
        { key: '/sales', icon: <TeamOutlined />, label: '영업' },
        { key: '/stores', icon: <ShopOutlined />, label: '매장' },
        { key: '/products', icon: <AppstoreOutlined />, label: '이용권 상품' },
      ],
    },
    { type: 'divider' },
    {
      key: 'transaction',
      type: 'group',
      label: '거래 · 정산',
      children: [
        { key: '/purchases', icon: <ShoppingCartOutlined />, label: '구매 내역' },
        { key: '/dashboard/settlement', icon: <DollarOutlined />, label: '정산' },
      ],
    },
    { type: 'divider' },
    {
      key: 'support',
      type: 'group',
      label: '고객 센터',
      children: [
        { key: '/notices', icon: <BellOutlined />, label: '공지사항' },
        { key: '/inquiries', icon: <MessageOutlined />, label: '1:1 문의' },
      ],
    },
    // 시스템 설정은 MASTER/SUPER_ADMIN만 노출
    ...(isSystemAdmin
      ? [
          { type: 'divider' as const },
          {
            key: 'system',
            type: 'group' as const,
            label: '시스템 설정',
            children: [
              { key: '/operators', icon: <TeamOutlined />, label: '운영자' },
              { key: '/vendors', icon: <TeamOutlined />, label: '게임 벤더' },
              { key: '/vendor-games', icon: <PlayCircleOutlined />, label: '게임 프로세스' },
              { key: '/factors', icon: <ControlOutlined />, label: 'Factor' },
            ],
          },
        ]
      : []),
  ];
};

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarActive } = useAppSelector(state => state.layout);
  const authUser = useAppSelector(state => state.auth.authUser);
  const { handleToggleSidebar } = useLayout();

  const menuItems = useMemo(
    () => getMenuItems(authUser?.role, authUser?.userId),
    [authUser?.role, authUser?.userId],
  );

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.startsWith('/stores/') && path.includes('/ips')) {
      return '/stores';
    }
    if (path.startsWith('/dashboard/settlement')) {
      return '/dashboard/settlement';
    }
    return path;
  };

  const handleMenuClick: MenuProps['onClick'] = e => {
    navigate(e.key);
    // 모바일에서 메뉴 클릭 시 사이드바 닫기
    if (window.innerWidth < breakpoints.md) {
      handleToggleSidebar();
    }
  };

  return (
    <aside css={[styles.sidebar, !isSidebarActive && styles.sidebarCollapsed]}>
      {/* Background Glow */}
      <div css={styles.bgGlow} />

      {/* 로고 영역 */}
      <Flex css={styles.logo} items="center" justify="space-between">
        <NavLink to="/">
          <Logo theme={ThemeMode.DARK} />{' '}
          <Heading style={{ letterSpacing: 0.07, color: 'rgba(255, 255, 255, 0.85)' }}>
            ADMIN
          </Heading>
        </NavLink>

        {/* 모바일 닫기 버튼 */}
        <button css={styles.closeButton} type="button" onClick={handleToggleSidebar}>
          <CloseOutlined />
        </button>
      </Flex>

      {/* 메뉴 영역 */}
      <Menu
        css={styles.menu}
        items={menuItems}
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        onClick={handleMenuClick}
      />

      {/* 하단 프로필 영역 */}
      <ProfileMenu />
    </aside>
  );
}

/* 부드러운 애니메이션을 위한 이징 함수 */
const EASING = {
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  smoothOut: 'cubic-bezier(0, 0, 0.2, 1)',
  smoothIn: 'cubic-bezier(0.4, 0, 1, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

const DURATION = {
  fast: '0.2s',
  normal: '0.3s',
  slow: '0.4s',
};

const styles = {
  /* 모바일: 고정 위치 + 100% 너비 / 데스크톱: 고정 너비 */
  sidebar: css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    min-width: 100%;
    height: 100vh;
    height: 100dvh;
    background: linear-gradient(180deg, #0a0c18 0%, #10122a 50%, #0d0f1a 100%);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 100;
    transition:
      width ${DURATION.normal} ${EASING.smooth},
      min-width ${DURATION.normal} ${EASING.smooth},
      transform ${DURATION.normal} ${EASING.smooth};

    ${breakpoint.md} {
      position: relative;
      width: ${LAYOUT.SIDE_BAR_WIDTH.EXPANDED};
      min-width: ${LAYOUT.SIDE_BAR_WIDTH.EXPANDED};
      border-right: 1px solid ${COLORS.GRAY['200']};
      z-index: auto;
    }
  `,

  /* 모바일: 화면 밖으로 이동 / 데스크톱: 너비 0 */
  sidebarCollapsed: css`
    transform: translateX(-100%);

    ${breakpoint.md} {
      transform: none;
      width: 0;
      min-width: 0;
      border-right: none;
    }
  `,

  /* 모바일: 닫기 버튼 표시 / 데스크톱: 숨김 */
  closeButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.65);
    font-size: 16px;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }

    ${breakpoint.md} {
      display: none;
    }
  `,

  bgGlow: css`
    position: absolute;
    width: 800px;
    max-width: 100%;
    height: 800px;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    filter: blur(140px);
    opacity: 0.8;
    pointer-events: none;
    background: radial-gradient(
      circle at 50% 35%,
      rgba(120, 80, 255, 0.55) 0%,
      rgba(80, 50, 180, 0.45) 35%,
      rgba(16, 10, 40, 1) 100%
    );
    background-blend-mode: screen;
    z-index: 0;
  `,

  menu: css`
    background: transparent;
    border-inline-end: none !important;
    padding: 8px;
    flex: 1;
    overflow-y: auto;
    z-index: 1;

    .ant-menu-item-divider {
      margin: 12px 8px;
      border-color: rgba(255, 255, 255, 0.1);
    }

    .ant-menu-item-group-title {
      padding: 8px 12px 4px;
      font-size: 11px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.4);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .ant-menu-item {
      margin: 4px 0;
      padding-left: 16px !important;
      border-radius: 8px;
      color: rgba(255, 255, 255, 0.65);
      transition: all 0.3s;
    }

    .ant-menu-item:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
      color: #fff !important;
    }

    .ant-menu-item-selected {
      background: linear-gradient(90deg, #6c5dd3 0%, #8b78e6 100%);
      color: #fff;
      box-shadow: 0 4px 15px rgba(108, 93, 211, 0.4);
    }

    .ant-menu-item-selected:hover {
      background: linear-gradient(90deg, #6c5dd3 0%, #8b78e6 100%) !important;
      color: #fff !important;
    }
  `,

  logo: css`
    display: flex;
    align-items: center;
    height: ${LAYOUT.NAV_BAR_HEIGHT};
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: ${SPACING['3']} ${SPACING['5']};
    ${safeArea('top')}
    color: #fff;
    z-index: 1;

    a {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  `,
};

export default Sidebar;
